import React from "react";
import { ShoppingCart } from "lucide-react";
import { Card, CardHeader, CardBody, CardTitle, CardText, Badge, Table } from "reactstrap";
import { extractTierFromName, getProductTierBadgeColor } from "../../../../../utility/inventoryUtils";
import './index.css'
import { ProductSummary } from "../../../../../hooks/useFirebaseProductSummaries";

interface FullPictureTabProps {
  productSummaries: ProductSummary[];
}

export const FullPictureTab: React.FC<FullPictureTabProps> = ({ productSummaries }) => {

  if (!productSummaries || productSummaries.length === 0) {
    return (
      <div className="text-center py-5">
        <div className="alert alert-warning">
          <h5>No product data available</h5>
          <p className="mb-0">No product summaries data found. Please check if data exists in Firebase.</p>
        </div>
      </div>
    );
  }

  // Get all unique channels across all carts in all products
  const getAllChannels = () => {
    const channels = new Set<string>();
    productSummaries.forEach(product => {
      Object.values(product.carts || {}).forEach(cart => {
        Object.keys(cart.channels || {}).forEach(channel => channels.add(channel));
      });
    });
    return Array.from(channels);
  };

  const allChannels = getAllChannels();

  // Function to format channel name for display
  const formatChannelName = (channelName: string) => {
    const formatMap: Record<string, string> = {
      web: "Web",
      webDelivery: "Web Delivery",
      whatsapp: "WhatsApp",
      walkin: "Walk-in",
      route: "Route",
      webPickup: "Web Pickup",
      pickMeUber: "PickMe Uber",
    };
    
    // Check if exact match exists
    if (formatMap[channelName]) return formatMap[channelName];
    
    // Try case-insensitive match
    const lowerName = channelName.toLowerCase();
    for (const [key, value] of Object.entries(formatMap)) {
      if (key.toLowerCase() === lowerName) return value;
    }
    
    // Return formatted name (capitalize first letter, add space before capital letters)
    return channelName
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  };

  // Function to get channel abbreviation for status badges
  const getChannelAbbr = (channelName: string) => {
    const map: Record<string, string> = {
      web: 'WE',
      webDelivery: 'WD',
      whatsapp: 'WA',
      walkin: 'WI',
      route: 'RT',
      webPickup: 'WP',
      pickMeUber: 'PU',
    };
    
    // Check if exact match exists
    if (map[channelName]) return map[channelName];
    
    // Try case-insensitive match
    const lowerName = channelName.toLowerCase();
    for (const [key, value] of Object.entries(map)) {
      if (key.toLowerCase() === lowerName) return value;
    }
    
    // Return first 2 letters uppercase
    return channelName.substring(0, 2).toUpperCase();
  };

  return (
    <div className="inventory-full-picture-tab-content">
      {/* Products Detail */}
      {productSummaries.map((product) => {
        const tier = extractTierFromName(product.displayName);
        
        // Calculate totals using actual properties
        const totalAllocated = Object.values(product.channels || {}).reduce((sum, channel) => sum + (channel.allocated || 0), 0);
        const totalOrders = Object.values(product.channels || {}).reduce((sum, channel) => sum + (channel.reserved || 0), 0);

        return (
          <Card key={product.id} className="inventory-full-picture-card mb-4 overflow-hidden border">
            <CardHeader className="inventory-full-picture-card-header bg-light">
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <Badge
                    color={getProductTierBadgeColor(tier)}
                    className="inventory-full-picture-tier-badge me-3"
                  >
                    {tier}
                  </Badge>
                  <div>
                    <CardTitle className="inventory-full-picture-product-name h5 mb-0">
                      {product.displayName}
                    </CardTitle>
                    <CardText className="inventory-full-picture-product-sku text-muted small mb-0">
                     {product.productId || "No Product ID"} | Allocated: {totalAllocated} | Orders: {totalOrders}
                    </CardText>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardBody className="inventory-full-picture-card-body p-0">
              <div className="inventory-full-picture-table-container">
                <Table responsive className="inventory-full-picture-table mb-0">
                  <thead className="inventory-full-picture-table-header bg-light">
                    <tr>
                      <th className="inventory-full-picture-table-header-cell" style={{ width: '20%' }}>Cart</th>
                      <th className="inventory-full-picture-table-header-cell text-center" style={{ width: '10%' }}>Total</th>
                      {allChannels.map(channel => (
                        <th key={channel} className="inventory-full-picture-table-header-cell text-center" style={{ width: `${70 / allChannels.length}%` }}>
                          {formatChannelName(channel)}
                        </th>
                      ))}
                      <th className="inventory-full-picture-table-header-cell text-center" style={{ width: '10%' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody className="inventory-full-picture-table-body">
                    {Object.entries(product.carts || {}).map(([cartId, cart]) => {
                      // Calculate cart total from cart.channels
                      const cartTotal = Object.values(cart.channels || {}).reduce((sum, channel) => sum + (channel.allocated || 0), 0);
                      
                      // Get channel alerts for this cart from cart.channels
                      const cartAlerts: Array<{channel: string, channelAbbr: string, available: number, type: 'over' | 'low'}> = [];
                      
                      return (
                        <tr key={cartId} className="inventory-full-picture-table-row">
                          <td className="inventory-full-picture-table-cell align-middle">
                            <div className="d-flex align-items-center">
                              <div className="bg-primary rounded-circle p-2 me-2">
                                <ShoppingCart className="text-white" size={16} />
                              </div>
                              <div>
                                <span className="inventory-full-picture-cart-id fw-bold text-uppercase d-block">
                                  {cart.cartName || cartId.toUpperCase()}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td className="inventory-full-picture-table-cell text-center align-middle">
                            <div className="inventory-full-picture-total-container">
                              <div className="inventory-full-picture-total-value h4 fw-bold mb-1">
                                {cartTotal}
                              </div>
                              <div className="inventory-full-picture-total-label text-muted small">
                                Total
                              </div>
                            </div>
                          </td>
                          {allChannels.map(channel => {
                            const channelData = cart.channels?.[channel];
                            if (!channelData) {
                              return (
                                <td key={channel} className="inventory-full-picture-table-cell text-center align-middle text-muted">
                                  -
                                </td>
                              );
                            }

                            const allocated = channelData.allocated || 0;
                            const orders = channelData.reserved || 0;
                            const available = channelData.available || 0;
                            const isLowStock = available < 3 && available >= 0;
                            const isOverCommitted = available < 0;
                            const channelAbbr = getChannelAbbr(channel);

                            if (isLowStock || isOverCommitted) {
                              cartAlerts.push({
                                channel: channel,
                                channelAbbr: channelAbbr,
                                available,
                                type: isOverCommitted ? 'over' : 'low'
                              });
                            }

                            return (
                              <td
                                key={channel}
                                className={`inventory-full-picture-table-cell text-center align-middle ${
                                  isOverCommitted
                                    ? "inventory-full-picture-cell-over-committed"
                                    : isLowStock
                                    ? "inventory-full-picture-cell-low-stock"
                                    : ""
                                }`}
                              >
                                <div className="inventory-full-picture-channel-container">
                                  <div className="inventory-full-picture-channel-value fw-bold h5 mb-2">
                                    {allocated}
                                  </div>
                                  <div className="inventory-full-picture-channel-details">
                                    <div className="inventory-full-picture-channel-detail-row mb-1">
                                      <span className="inventory-full-picture-channel-detail-orders">{orders}</span>
                                      <span className="inventory-full-picture-channel-detail-label">ord</span> /
                                      <span className={`inventory-full-picture-channel-detail-available ${
                                        isOverCommitted
                                          ? "text-danger"
                                          : isLowStock
                                          ? "text-warning"
                                          : "text-success"
                                      }`}>
                                        {available}
                                      </span>
                                      <span className="inventory-full-picture-channel-detail-label">avl</span>
                                    </div>
                                  </div>
                                </div>
                              </td>
                            );
                          })}
                          <td className="inventory-full-picture-table-cell text-center align-middle">
                            {cartAlerts.length === 0 ? (
                              <Badge color="success" className="inventory-full-picture-status-badge p-2">
                                Healthy
                              </Badge>
                            ) : (
                              <div className="inventory-full-picture-alerts-container">
                                {cartAlerts.map((alert, index) => (
                                  <div 
                                    key={`${alert.channel}-${index}`}
                                    className={`inventory-full-picture-alert-badge d-inline-block me-1 mb-1 ${
                                      alert.type === 'over' 
                                        ? 'inventory-full-picture-alert-over' 
                                        : 'inventory-full-picture-alert-low'
                                    }`}
                                  >
                                    {alert.channelAbbr} {alert.type}
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        );
      })}
    </div>
  );
};